import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { LocalizedString, Locale } from '@types';
import { getLocalizedString } from './localization';

/**
 * Renders markdown content with GitHub Flavored Markdown support
 * @param content - The markdown string to render
 * @param className - Optional CSS class for the container
 * @returns React component with rendered markdown
 */
export const renderMarkdown = (
  content: string,
  className?: string
) => {
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom component renderers for better styling control
          h1: ({ children }) => <h3>{children}</h3>, // Downgrade h1 to h3 for option descriptions
          h2: ({ children }) => <h4>{children}</h4>, // Downgrade h2 to h4
          h3: ({ children }) => <h5>{children}</h5>, // Downgrade h3 to h5
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          // Ensure lists have proper spacing
          ul: ({ children }) => <ul className="markdown-list">{children}</ul>,
          ol: ({ children }) => <ol className="markdown-list">{children}</ol>,
          // Code blocks
          code: ({ children, ...props }) => {
            const isInline = !props.className;
            if (isInline) {
              return <code className="inline-code">{children}</code>;
            }
            return (
              <pre className="code-block">
                <code className={props.className}>{children}</code>
              </pre>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

/**
 * Renders localized markdown content
 * @param localizedContent - Localized markdown strings
 * @param locale - Current locale
 * @param className - Optional CSS class for the container
 * @returns React component with rendered markdown
 */
export const renderLocalizedMarkdown = (
  localizedContent: LocalizedString | undefined,
  locale: Locale,
  className?: string
) => {
  const content = getLocalizedString(localizedContent, locale);

  if (!content) {
    return null;
  }

  return renderMarkdown(content, className);
};